import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormElement, Field, Form } from '@progress/kendo-react-form'
import { MultiFactorVerifiedDto, VerifySmsMultiFactorCodeDto } from '@v1UsersApi'
import { DefaultButton } from 'components'
import MessageAlert from 'components/Alerts/MessageAlert/MessageAlert'
import FormInput from 'components/Form/FormInput'
import { RequiredValidator } from 'core/Validators'
import { getErrors, focusFirstError, hasStatusCode } from 'core/helpers'
import { V1ApiUsersClient } from 'core/http-common'
import { useState, useRef, useEffect } from 'react'
import { Row, Col, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { startLoadingAction, endLoadingAction } from 'state/actions/appActions'
import { setMultFactorVerified } from 'state/actions/userActions'
import { AppDispatch } from 'state/store'
import { IRootState, AlertMessage } from 'types'

interface IFormState {
    code: string
}

interface IProps {
    onVerified?: (dto: MultiFactorVerifiedDto) => void
}

const SmsMultiFactorVerifyForm = ({ onVerified }: IProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const loading = useSelector((state: IRootState) => state.app.loading)
    const [alert, setAlert] = useState<AlertMessage>()
    const [multiFactorCodeSent, setMultiFactorCodeSent] = useState(false)
    const [multiFactorCodeExpired, setMultiFactorCodeExpired] = useState(false)

    const requestMultiFactorCode = async () => {
        try {
            dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let response = await V1ApiUsersClient.sendSmsMultiFactorCode()
            setMultiFactorCodeSent(true)
            setMultiFactorCodeExpired(false)
            setAlert({
                variant: 'success',
                messages: [`2FA Code sent to phone ${response.mobilePhone} and email ${response.email}, it expires in: ${response.expireMinutes} minutes.`]
            })
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    const submitCode = async (values: IFormState) => {
        let code = values.code
        if (!code)
            return

        try {
            dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let response = await V1ApiUsersClient.verifySmsMultiFactorCode(new VerifySmsMultiFactorCodeDto({ code }))
            dispatch(setMultFactorVerified(response))
            if (onVerified)
                onVerified(response)
        }
        catch (e) {
            if (hasStatusCode(e, 409)) {
                setAlert({ variant: 'warning', messages: [' Code expired!'] })
                setMultiFactorCodeExpired(true)
            }
            else if (hasStatusCode(e, 404)) {
                setAlert({ variant: 'danger', messages: ['Wrong code entered!'] })
            }
            else {
                setAlert({ variant: 'danger', messages: getErrors(e) })
            }
        }
        finally {
            await dispatch(endLoadingAction())
        }
    }

    useEffect(() => {
        let input = document.getElementById('code')
        input?.focus()

    }, [multiFactorCodeSent])

    return (
        <div>
            <Row>
                <Col>
                    <div>
                        <div className='mb-2'>
                            <b>Text Message:</b>
                        </div>

                        {multiFactorCodeExpired || !multiFactorCodeSent
                            ? <div>
                                <DefaultButton
                                    className='w-100'
                                    type='button'
                                    onClick={requestMultiFactorCode}
                                    variant='primary'
                                    disabled={loading}
                                >
                                    Send Code{' '}
                                    <FontAwesomeIcon icon={['fas', 'unlock']} />
                                </DefaultButton>

                            </div>
                            : <Form
                                onSubmit={(e) => submitCode(e as IFormState)}
                                render={(formRenderProps) => (
                                    <FormElement>
                                        <Row className='mb-2'>
                                            <Col xs={12}>
                                                <Field
                                                    component={FormInput}
                                                    id='code'
                                                    name='code'
                                                    label='Code:'
                                                    required
                                                    validator={RequiredValidator}
                                                    disabled={loading}
                                                    autofocus
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12}>
                                                <Button
                                                    className='w-100'
                                                    variant='primary'
                                                    type='submit'
                                                    disabled={!formRenderProps.allowSubmit || loading}
                                                    onClick={e => focusFirstError(formRenderProps)}>
                                                    <FontAwesomeIcon icon={['fas', 'arrow-circle-right']} /> Verify Code
                                                </Button>

                                            </Col>
                                        </Row>
                                    </FormElement>
                                )}
                            />
                        }
                    </div>
                </Col>
            </Row>

            <MessageAlert alert={alert} />
        </div>
    )
}
export default SmsMultiFactorVerifyForm